import _ from 'lodash'
import type { Materializer } from '@wix/materializer/src/types'
import type { RefBeckyModel } from '@wix/thunderbolt-becky-root'
import type { Data, DataMapName, Structure, Language } from '@wix/thunderbolt-becky-types'
import { getByRefsis } from './byRefsis/byRefsis'
import { DATA_MAP_NAMES } from './byRefsis/constants'

type Translations = RefBeckyModel['translations']

const MAPS_WITH_DATA_REFS: Array<DataMapName> = ['document_data', 'design_data']

export const resolveByRef = (beckyModel: RefBeckyModel) => {
	const {
		structure,
		data,
		masterPageData,
		translations,
		masterPageTranslations,
		experiments,
		viewMode,
		widgetTemplatesStore,
		resolveGhosts: keepGhostComponents,
		originalLanguage,
		language,
	} = beckyModel

	const store: { structure: Structure; data: Data; translations: Translations } = {
		structure: { ...structure },
		data: {} as Data,
		// TODO the idea was to apply translations in byrefsis and pass an empty map to becky,
		// but this thing: https://github.com/wix-private/thunderbolt/blob/1e0c9333c78848aa9abb45aa23b8491618cb0b9b/packages/thunderbolt-becky/src/carmi/apis/seoApi.carmi.ts
		// needs it. discussed here: https://wix.slack.com/archives/CV5L8P7H6/p1662025358889569?thread_ts=1661866741.406359&cid=CV5L8P7H6
		// consider passing "original page translations + original page document data items" to becky
		translations: {} as Translations,
	}

	const onInvalidation = (path: Array<string>, get: Materializer['get']) => {
		const [, aspect, id] = path
		if (aspect === 'structure') {
			_.set(store, ['structure', id], get(path))
		} else if (DATA_MAP_NAMES.has(aspect as DataMapName)) {
			_.set(store, ['data', aspect, id], get(path))
		} else if (aspect === 'layoutQueryInflatedMap') {
			_.set(store, ['data', 'layout_data'], Object.assign(store.data.layout_data || {}, get(path)))
		} else if (aspect === 'styleIdInflatedMap') {
			_.set(store, ['data', 'theme_data'], Object.assign(store.data.theme_data || {}, get(path)))
		} else {
			_.set(store, path, get(path))
		}
	}

	const widgetTemplatesMapsWithDataRefs: Partial<Data> = {}

	const byRefsis = getByRefsis({ onInvalidation })
	byRefsis.transaction(
		({
			updateStructure,
			updateData,
			updateTranslation,
			updateWidgetStructure,
			updateWidgetData,
			updateWidgetTranslation,
			updateWidgetRootCompId,
			updateEnvironment,
		}) => {
			updateEnvironment({
				experiments,
				environment: { viewMode, keepGhostComponents, originalLanguage, language },
			})

			for (const compId in structure) {
				updateStructure(compId, structure[compId])
			}

			for (const dataMapName in data) {
				for (const id in data[dataMapName as DataMapName]) {
					const dataItem = data[dataMapName as DataMapName][id]
					updateData(dataMapName as DataMapName, id, dataItem)
				}
			}

			for (const translationLanguage in masterPageTranslations) {
				const translatedDataItems = masterPageTranslations[translationLanguage as Language].data.document_data
				for (const documentDataItemId in translatedDataItems) {
					updateTranslation(
						translationLanguage as Language,
						documentDataItemId,
						translatedDataItems[documentDataItemId]
					)
				}
			}

			for (const translationLanguage in translations) {
				const translatedDataItems = translations[translationLanguage as Language].data.document_data
				for (const documentDataItemId in translatedDataItems) {
					updateTranslation(
						translationLanguage as Language,
						documentDataItemId,
						translatedDataItems[documentDataItemId]
					)
				}
			}

			for (const templateKey in widgetTemplatesStore[viewMode]) {
				const widgetTemplate = widgetTemplatesStore[viewMode][templateKey]
				updateWidgetRootCompId(templateKey, widgetTemplate.rootId)

				for (const widgetTranslationsLanguage in widgetTemplate.translations) {
					const translatedDataItems =
						widgetTemplate.translations[widgetTranslationsLanguage as Language].data.document_data
					for (const documentDataItemId in translatedDataItems) {
						updateWidgetTranslation(
							templateKey,
							widgetTranslationsLanguage as Language,
							documentDataItemId,
							translatedDataItems[documentDataItemId]
						)
					}
				}

				for (const compId in widgetTemplate.structure) {
					updateWidgetStructure(templateKey, compId, widgetTemplate.structure[compId])
				}

				for (const dataMapName in widgetTemplate.data) {
					const dataMap = widgetTemplate.data[dataMapName as DataMapName]
					for (const id in dataMap) {
						updateWidgetData(templateKey, dataMapName as DataMapName, id, dataMap[id])

						if (MAPS_WITH_DATA_REFS.includes(dataMapName as DataMapName)) {
							_.set(widgetTemplatesMapsWithDataRefs, [dataMapName, id], dataMap[id])
						}
					}
				}
			}
		}
	)

	const mergedData = _.reduce(
		_.keys(data) as Array<keyof Data>,
		(result, dataMapName) => {
			// @ts-ignore
			result[dataMapName] = _.assign(
				widgetTemplatesMapsWithDataRefs[dataMapName],
				data[dataMapName],
				MAPS_WITH_DATA_REFS.includes(dataMapName) ? masterPageData?.[dataMapName] : {},
				store.data[dataMapName]
			)

			return result
		},
		{} as Data
	)

	return {
		structure: store.structure,
		data: mergedData,
		translations: store.translations,
	}
}
